import { c as _c } from "react/compiler-runtime";
import OptimizedImage from '@/components/OptimizedImage';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { ChangeEvent, Dispatch, SetStateAction, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import ViverseButton from '@/components/ViverseButton';
import ViverseDialog from '@/components/ViverseDialog';
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ACCEPTED_IMAGE_TYPES, MAX_IMAGE_SIZE } from '@/constants/file';
import { useConfigsContext } from '@/context/ConfigsContext';
import { useUploadErrorMessages } from '@/hooks/useUploadErrorMessages';
import { extractIdFromImageUrl } from '@/lib/asset';
import { validateImageDimensions } from '@/lib/image';
import { showToast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { updateProfileMeByCmsAccountId } from '@/server/lib/profile';
import { uploadImageAction } from '@/server/lib/upload';
const useFormSchema = () => {
  const $ = _c(2);
  const errorMessages = useUploadErrorMessages();
  let t0;
  if ($[0] !== errorMessages) {
    t0 = z.object({
      avatar: z.instanceof(File).refine(_temp, {
        message: errorMessages["profile-avatar-file-too-large"].description
      }).nullable()
    });
    $[0] = errorMessages;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const formSchema = t0;
  return formSchema;
};
type EditAvatarDialogProps = {
  defaultAvatarUrl: string;
  isEditing: boolean;
  setIsEditing: Dispatch<SetStateAction<boolean>>;
};
function EditAvatarDialog({
  defaultAvatarUrl,
  isEditing,
  setIsEditing
}: EditAvatarDialogProps) {
  const t = useTranslations();
  const {
    store,
    setStore
  } = useConfigsContext();
  const formSchema = useFormSchema();
  const errorMessages = useUploadErrorMessages();
  const htcToken = store?.credentials?.htcToken;
  const cmsAccountId = store?.profile?.id;
  const fileInput = useRef<HTMLInputElement>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      avatar: null
    }
  });
  const resetForm = () => {
    form.reset();
    form.clearErrors('avatar');
    setPreviewImage(null);
  };
  const handleCancel = () => {
    setIsEditing(false);
    resetForm();
  };
  const handleOpenChange = (open: boolean) => {
    if (isSaving) return;
    setIsEditing(open);
    if (!open) {
      resetForm();
    }
  };
  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>, onChange: (file: File) => void) => {
    if (!event.target.files) return;
    const file = event.target.files[0];
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type as (typeof ACCEPTED_IMAGE_TYPES)[number])) {
      showToast({
        message: errorMessages['unsupported-file-type-1'].description,
        title: errorMessages['unsupported-file-type-1'].title,
        type: 'error'
      });
    } else if (file.size > MAX_IMAGE_SIZE) {
      showToast({
        message: errorMessages['profile-avatar-file-too-large'].description,
        title: errorMessages['profile-avatar-file-too-large'].title,
        type: 'error'
      });
    } else {
      const dimensionValidation = await validateImageDimensions(file);
      if (dimensionValidation === 'unreadable') {
        showToast({
          message: errorMessages['unsupported-file-type-1'].description,
          title: errorMessages['unsupported-file-type-1'].title,
          type: 'error'
        });
        return;
      }
      if (dimensionValidation === 'too-large') {
        showToast({
          message: errorMessages['image-dimensions-too-large'].description,
          title: errorMessages['image-dimensions-too-large'].title,
          type: 'error'
        });
        return;
      }
      form.clearErrors('avatar');
      onChange(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };
  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (!htcToken || !cmsAccountId) {
      throw new Error('No HTC token or CMS account ID');
    }
    setIsSaving(true);
    try {
      const uploadResult = await uploadImageAction(data.avatar!);
      if (!uploadResult.ok) throw new Error(uploadResult.error);
      const avatarUrl = uploadResult.url;
      const res = await updateProfileMeByCmsAccountId(cmsAccountId, {
        profile_image: extractIdFromImageUrl(avatarUrl)
      });
      if (res?.status === 200 && res?.results) {
        setStore(value => ({
          ...value,
          profile: {
            ...value.profile!,
            image: avatarUrl
          }
        }));
      } else {
        throw new Error('Failed to update profile avatar');
      }
    } catch (error) {
      console.error('Failed to update profile avatar: ', error);
    } finally {
      setIsSaving(false);
      setIsEditing(false);
      resetForm();
    }
  };
  return <ViverseDialog contentClassName="pb-5 max-md:w-full md:min-w-[600px]" triggerClassName="lg:contents" title="Upload Profile Picture" open={isEditing} onClose={handleCancel} onOpenChange={handleOpenChange} clickOutsideToClose={false}>
      <Form {...form}>
        <form className="flex w-full flex-col" onSubmit={form.handleSubmit(onSubmit)}>
          <FormField control={form.control} name="avatar" render={({
          field: {
            onChange: onChange_0
          }
        }) => <FormItem>
                <FormControl>
                  <Label htmlFor="avatar" className="cursor-pointer">
                    <div className="bg-vive-brand-60 relative m-auto mb-6 size-[180px] overflow-hidden rounded-full">
                      <OptimizedImage src={previewImage || defaultAvatarUrl} className="object-cover" alt="profile avatar" fill sizes="180px" />
                    </div>
                    <Input type="file" className="hidden" accept={ACCEPTED_IMAGE_TYPES.join(',')} ref={fileInput} onChange={event_0 => handleFileChange(event_0, onChange_0)} id="avatar" />
                  </Label>
                </FormControl>
              </FormItem>} />
          <div className="m-auto w-fit">
            <ViverseButton variant="secondary" className="mr-4 min-h-[46px] min-w-fit md:min-h-12 md:text-[18px]" onClick={handleCancel}>
              {t('button.cancel')}
            </ViverseButton>
            <ViverseButton className="min-h-[46px] min-w-fit md:min-h-12 md:text-[18px]" type="submit" disabled={!!(isSaving || !form.watch('avatar') || form.formState.errors.avatar)}>
              {t('button.apply')}
            </ViverseButton>
          </div>
        </form>
      </Form>
      <div className={cn('z-navigation absolute inset-0 hidden animate-pulse bg-gray-800/30', {
      'block animate-pulse cursor-wait': isSaving
    })} />
    </ViverseDialog>;
}
export default EditAvatarDialog;
function _temp(file) {
  return file.size < MAX_IMAGE_SIZE;
}